import React from "react";
import { DeploySidebar } from "./DeploySidebar";
import {
    HostedApiContent,
    BatchProcessingContent,
    LocalServerContent,
    VideoLocalContent,
    VideoApiContent
} from "./content";
import { RunWorkflowModalContentProps } from "./types";

function ImageModeContent({
    currentImageMode,
    workflowData,
    selectedLanguage,
    onLanguageSelect,
    onOpenBatchModal,
    onClose,
    isWorkflowEditor,
    onOpenTerminalScript,
    hasExistingJobs,
    onViewJobs,
    onOpenPreview
}: Pick<
    RunWorkflowModalContentProps,
    | "workflowData"
    | "selectedLanguage"
    | "onLanguageSelect"
    | "onOpenBatchModal"
    | "onClose"
    | "isWorkflowEditor"
    | "onOpenTerminalScript"
    | "hasExistingJobs"
    | "onViewJobs"
    | "onOpenPreview"
> & { currentImageMode: string }) {
    switch (currentImageMode) {
        case "hosted":
            return (
                <HostedApiContent
                    selectedLanguage={selectedLanguage}
                    onLanguageSelect={onLanguageSelect}
                    endpointUrl={workflowData.endpointUrl}
                    workflowUrl={workflowData.workflowUrl}
                    workspaceUrl={workflowData.workspaceUrl}
                    apiKey={workflowData.apiKey}
                    workflowParameters={workflowData.workflowParameters}
                    workflowImages={workflowData.workflowImages}
                    isLocalEmbedded={workflowData.isLocalEmbedded}
                    isWorkflowEditor={isWorkflowEditor}
                    onOpenPreview={onOpenPreview}
                />
            );

        case "in-app":
            return (
                <BatchProcessingContent
                    dataType="images"
                    workflowInfo={{
                        workflowUrl: workflowData.workflowUrl,
                        workflowName: workflowData.workflowName
                    }}
                    onOpenBatchModal={onOpenBatchModal}
                    onClose={onClose}
                    isWorkflowEditor={isWorkflowEditor}
                    onOpenTerminalScript={onOpenTerminalScript}
                    hasExistingJobs={hasExistingJobs}
                    onViewJobs={onViewJobs}
                />
            );

        case "local":
            return (
                <LocalServerContent
                    workflowUrl={workflowData.workflowUrl}
                    workspaceUrl={workflowData.workspaceUrl}
                    apiKey={workflowData.apiKey}
                    workflowParameters={workflowData.workflowParameters}
                    workflowImages={workflowData.workflowImages}
                    localDeployUrl={workflowData.localDeployUrl}
                    selectedLanguage={selectedLanguage}
                    onLanguageSelect={onLanguageSelect}
                    isLocalEmbedded={workflowData.isLocalEmbedded}
                    isWorkflowEditor={isWorkflowEditor}
                />
            );

        default:
            return null;
    }
}

function VideoModeContent({
    currentVideoMode,
    workflowData,
    onOpenBatchModal,
    onClose,
    isWorkflowEditor,
    onOpenTerminalScript,
    hasExistingJobs,
    onViewJobs
}: Pick<
    RunWorkflowModalContentProps,
    | "workflowData"
    | "onOpenBatchModal"
    | "onClose"
    | "isWorkflowEditor"
    | "onOpenTerminalScript"
    | "hasExistingJobs"
    | "onViewJobs"
> & { currentVideoMode: string }) {
    switch (currentVideoMode) {
        case "api":
            return (
                <VideoApiContent
                    workflowUrl={workflowData.workflowUrl}
                    workspaceUrl={workflowData.workspaceUrl}
                    apiKey={workflowData.apiKey}
                    workflowParameters={workflowData.workflowParameters}
                    isLocalEmbedded={workflowData.isLocalEmbedded}
                    workflowConfigJSON={workflowData.workflowConfigJSON}
                    isWorkflowEditor={isWorkflowEditor}
                />
            );

        case "in-app":
            return (
                <BatchProcessingContent
                    dataType="video"
                    workflowInfo={{
                        workflowUrl: workflowData.workflowUrl,
                        workflowName: workflowData.workflowName
                    }}
                    onOpenBatchModal={onOpenBatchModal}
                    onClose={onClose}
                    isWorkflowEditor={isWorkflowEditor}
                    onOpenTerminalScript={onOpenTerminalScript}
                    hasExistingJobs={hasExistingJobs}
                    onViewJobs={onViewJobs}
                />
            );

        case "local":
            return (
                <VideoLocalContent
                    workflowUrl={workflowData.workflowUrl}
                    workspaceUrl={workflowData.workspaceUrl}
                    apiKey={workflowData.apiKey}
                    workflowParameters={workflowData.workflowParameters}
                    isLocalEmbedded={workflowData.isLocalEmbedded}
                    workflowConfigJSON={workflowData.workflowConfigJSON}
                    isWorkflowEditor={isWorkflowEditor}
                />
            );

        default:
            return null;
    }
}

export function RunWorkflowModalContent({
    workflowData,
    dataType,
    currentImageMode,
    currentVideoMode,
    onDataTypeChange,
    onImageModeChange,
    onVideoModeChange,
    selectedLanguage,
    onLanguageSelect,
    onOpenBatchModal,
    onClose,
    contentRef,
    isWorkflowEditor = true,
    onOpenTerminalScript,
    hasExistingJobs,
    onViewJobs,
    onOpenPreview
}: RunWorkflowModalContentProps) {
    return (
        <>
            <DeploySidebar
                dataType={dataType}
                currentImageMode={currentImageMode}
                currentVideoMode={currentVideoMode}
                onDataTypeChange={onDataTypeChange}
                onImageModeChange={onImageModeChange}
                onVideoModeChange={onVideoModeChange}
                isWorkflowEditor={isWorkflowEditor}
            />

            <div ref={contentRef} className="sidebarTabContent">
                {dataType === 0 ? (
                    <ImageModeContent
                        currentImageMode={currentImageMode}
                        workflowData={workflowData}
                        selectedLanguage={selectedLanguage}
                        onLanguageSelect={onLanguageSelect}
                        onOpenBatchModal={onOpenBatchModal}
                        onClose={onClose}
                        isWorkflowEditor={isWorkflowEditor}
                        onOpenTerminalScript={onOpenTerminalScript}
                        hasExistingJobs={hasExistingJobs}
                        onViewJobs={onViewJobs}
                        onOpenPreview={onOpenPreview}
                    />
                ) : (
                    <VideoModeContent
                        currentVideoMode={currentVideoMode}
                        workflowData={workflowData}
                        onOpenBatchModal={onOpenBatchModal}
                        onClose={onClose}
                        isWorkflowEditor={isWorkflowEditor}
                        hasExistingJobs={hasExistingJobs}
                        onViewJobs={onViewJobs}
                        onOpenTerminalScript={onOpenTerminalScript}
                    />
                )}
            </div>
        </>
    );
}
